import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  Button,
  Input,
  Row,
  Col,
  Card,
  Space,
  Dropdown,
  Tag,
  Spin,
  Menu,
  Modal,
} from "antd";
import {
  EyeOutlined,
  FlagOutlined,
  DownOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { ChevronDown } from "react-feather";
import moment from "moment";
import Select from "react-select";
import axios from "axios";
import { useNotification } from "../../../context/NotificationContext";
import exportService from "../../../pages/pos/utils/exportService";
import PrintReportPreview from "../../../pages/pos/reports/PrintReportPreview";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import Swal from "sweetalert2";
import SurveySelect from "./components/SurveySelect";
import { useLocation } from "react-router-dom";
const ResponseList = ({ surveyId: initialSurveyId, surveyTitle: initialSurveyTitle }) => {
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    dateRange: {
      startDate: moment().startOf("month"),
      endDate: moment().endOf("month"),
    },
    rating: null,
    nps: null,
    promoterOnly: false,
    detractorOnly: false,
    passiveOnly: false,
    csat: null,
    status: null,
    surveyId: initialSurveyId || null,
  });
  const [responses, setResponses] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");
  const { Search } = Input;
  const authHeaders = () => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  });

  const location = useLocation();
    useEffect(() => {
    const params = new URLSearchParams(location.search);
    const surveyParam = params.get("survey");

    if (surveyParam) {
      setFilters((prev) => ({ ...prev, surveyId: surveyParam }));
    }
  }, [location.search]);


  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error("Failed to enter fullscreen:", err);
        });
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen:", err);
        });
        setFullScreen(false);
      }
    };
    const handleFullscreenChange = () => {
      setFullScreen(!!document.fullscreenElement);
    };
    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener("click", handleClick);
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener("click", handleClick);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen during cleanup:", err);
        });
      }
    };
  }, []);

  // Fetch responses
  const fetchResponses = async () => {
    if (!filters.surveyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}/feedback/surveys/${filters.surveyId}/responses`,
        {
          ...authHeaders(),
          params: {
            startDate: filters.dateRange.startDate.format("YYYY-MM-DD"),
            endDate: filters.dateRange.endDate.format("YYYY-MM-DD"),
            search: filters.search || "",
          },
        }
      );
      const data = Array.isArray(res.data.responses || res.data)
        ? res.data.responses || res.data
        : [];
      setResponses(
        data.map((r) => ({
          id: r.responseid,
          guest_name: r.guest_name,
          email: r.email,
          mobile: r.mobile,
          birthday: r.birthday,
          anniversary: r.anniversary,
          rating: r.overall_rating,
          nps: r.nps_score,
          csat: r.csat >= 70 ? "positive" : "negative",
          is_promoter: r.is_promoter,
          is_detractor: r.is_detractor,
          is_passive: r.is_passive,
          dateSubmitted: r.created_at,
          status: r.status || "Pending", // Assuming API might have status, fallback to Pending
          comments: r.response_json?.comments || "",
        }))
      );
    } catch (err) {
      console.error("Error fetching responses:", err);
      notify("error", "Failed to load responses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResponses();
  }, [filters.surveyId, filters.dateRange, filters.search]);

  const filteredData = useMemo(() => {
    let result = responses;
    if (filters.search) {
      result = result.filter((r) =>
        (r.guest_name?.toLowerCase() || "").includes(filters.search.toLowerCase())
      );
    }
    if (filters.dateRange.startDate && filters.dateRange.endDate) {
      result = result.filter((r) =>
        moment(r.dateSubmitted).isBetween(
          filters.dateRange.startDate,
          filters.dateRange.endDate,
          undefined,
          "[]"
        )
      );
    }
    if (filters.rating) result = result.filter((r) => r.rating === parseInt(filters.rating));
    if (filters.nps) result = result.filter((r) => r.nps === parseInt(filters.nps));
    if (filters.promoterOnly) result = result.filter((r) => r.is_promoter);
    if (filters.detractorOnly) result = result.filter((r) => r.is_detractor);
    if (filters.passiveOnly) result = result.filter((r) => r.is_passive);
    if (filters.csat) result = result.filter((r) => r.csat === filters.csat);
    if (filters.status) result = result.filter((r) => r.status === filters.status);
    return result;
  }, [responses, filters]);

  const summaryStats = useMemo(() => {
    const total = filteredData.length;
    const avgRating = filteredData.reduce((s, r) => s + r.rating, 0) / (total || 1);
    const avgNPS = filteredData.reduce((s, r) => s + r.nps, 0) / (total || 1);
    return {
      totalResponses: total,
      averageRating: Number(avgRating.toFixed(1)),
      averageNPS: Number(avgNPS.toFixed(1)),
    };
  }, [filteredData]);

  const statusOptions = [
    { value: "Reviewed", label: "Reviewed" },
    { value: "Pending", label: "Pending" },
    { value: "Flagged", label: "Flagged" },
  ];

  const csatOptions = [
    { value: "positive", label: "Positive" },
    { value: "negative", label: "Negative" },
  ];

  const ratingOptions = [
    { value: "1", label: "1 ⭐" },
    { value: "2", label: "2 ⭐" },
    { value: "3", label: "3 ⭐" },
    { value: "4", label: "4 ⭐" },
    { value: "5", label: "5 ⭐" },
  ];

  const npsOptions = Array.from({ length: 11 }, (_, i) => ({
    value: i.toString(),
    label: `${i}/10`,
  }));

  const activeTags = [
    filters.promoterOnly && { label: "Promoters", onClose: () => setFilters({ ...filters, promoterOnly: false }), color: "green" },
    filters.detractorOnly && { label: "Detractors", onClose: () => setFilters({ ...filters, detractorOnly: false }), color: "red" },
    filters.passiveOnly && { label: "Passives", onClose: () => setFilters({ ...filters, passiveOnly: false }), color: "orange" },
    filters.csat && { label: `CSAT: ${filters.csat}`, onClose: () => setFilters({ ...filters, csat: null }), color: filters.csat === "positive" ? "blue" : "volcano" },
    filters.search && { label: `Search: ${filters.search}`, onClose: () => setFilters({ ...filters, search: "" }), color: "purple" },
    filters.dateRange.startDate && filters.dateRange.endDate && {
      label: `Date: ${filters.dateRange.startDate.format("DD MMM YYYY")} - ${filters.dateRange.endDate.format("DD MMM YYYY")}`,
      onClose: () => setFilters({ ...filters, dateRange: { startDate: moment().startOf("month"), endDate: moment().endOf("month") } }),
      color: "cyan",
    },
    filters.rating && { label: `Rating: ${filters.rating}⭐`, onClose: () => setFilters({ ...filters, rating: null }), color: "gold" },
    filters.nps && { label: `NPS: ${filters.nps}`, onClose: () => setFilters({ ...filters, nps: null }), color: "geekblue" },
  ].filter(Boolean);

  const headers = [
    { key: "guest_name", title: "Guest Name" },
    { key: "email", title: "Email" },
    { key: "mobile", title: "Mobile" },
    { key: "birthday", title: "Birthday" },
    { key: "anniversary", title: "Anniversary" },
    { key: "rating", title: "Rating" },
    { key: "nps", title: "NPS" },
    { key: "csat", title: "CSAT" },
    { key: "dateSubmitted", title: "Date Submitted" },
  ];

  const exportData = useMemo(() => {
    return filteredData.map((r) => ({
      guest_name: r.guest_name,
      email: r.email,
      mobile: r.mobile,
      birthday: r.birthday ? moment(r.birthday).format("DD MMM YYYY") : "-",
      anniversary: r.anniversary ? moment(r.anniversary).format("DD MMM YYYY") : "-",
      rating: `${r.rating} ⭐`,
      nps: `${r.nps}/10`,
      csat: r.csat?.toUpperCase() || "-",
      dateSubmitted: moment(r.dateSubmitted).format("DD MMM YYYY, h:mm A"),
    }));
  }, [filteredData]);

  const totalsForPageFooter = [
    { key: "Total Responses", value: summaryStats.totalResponses },
    { key: "Average Rating", value: `${summaryStats.averageRating} ⭐` },
    { key: "Average NPS", value: `${summaryStats.averageNPS}/10` },
  ];

  const exportFooter = totalsForPageFooter;

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData,
      headers,
      `responses${suffix}.xlsx`,
      "Responses"
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData,
      headers,
      `responses${suffix}.csv`
    );
  };

  const handleExportPDF = () => {
    exportService.exportToPDF(
      exportData,
      headers,
      "Responses",
      `responses${suffix}.pdf`,
      false,
      true,
      totalsForPageFooter,
      exportFooter
    );
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const pdfBlob = await exportService.exportToPDF(
        exportData,
        headers,
        "Responses",
        null,
        true,
        true,
        totalsForPageFooter,
        exportFooter
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPreviewVisible(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      notify("error", "Failed to load PDF preview.");
    } finally {
      setLoading(false);
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        <i className="la la-file-csv me-2"></i> Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        <i className="la la-file-excel me-2"></i> Download XLS
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        <i className="la la-print me-2"></i> Print Preview
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      key: "guest_name",
      render: (name) => <Tag color="blue">{name || "-"}</Tag>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <Tag color="blue">{email || "-"}</Tag>,
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      render: (mobile) => <Tag color="cyan">{mobile || "-"}</Tag>,
    },
    {
      title: "Birthday",
      dataIndex: "birthday",
      key: "birthday",
      render: (val) => <Tag color="purple">{val ? moment(val).format("DD MMM YYYY") : "-"}</Tag>,
    },
    {
      title: "Anniversary",
      dataIndex: "anniversary",
      key: "anniversary",
      render: (val) => <Tag color="purple">{val ? moment(val).format("DD MMM YYYY") : "-"}</Tag>,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      render: (rating) =>
        rating ? (
          <Tag color={rating >= 4 ? "green" : rating <= 2 ? "red" : "orange"}>
            {rating} ⭐
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "NPS",
      dataIndex: "nps",
      key: "nps",
      render: (nps) =>
        nps !== null && nps !== undefined ? (
          <Tag color={nps >= 9 ? "green" : nps <= 6 ? "red" : "orange"}>
            {nps}/10
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "CSAT",
      dataIndex: "csat",
      key: "csat",
      render: (val) => (
        <Tag color={val === "positive" ? "blue" : "volcano"}>
          {val?.toUpperCase() || "-"}
        </Tag>
      ),
    },
    {
      title: "Promoter",
      dataIndex: "is_promoter",
      key: "is_promoter",
      render: (val) => <Tag color="green">{val ? "Yes" : "-"}</Tag>,
    },
    {
      title: "Detractor",
      dataIndex: "is_detractor",
      key: "is_detractor",
      render: (val) => <Tag color="red">{val ? "Yes" : "-"}</Tag>,
    },
    {
      title: "Passive",
      dataIndex: "is_passive",
      key: "is_passive",
      render: (val) => <Tag color="orange">{val ? "Yes" : "-"}</Tag>,
    },
    {
      title: "Date Submitted",
      dataIndex: "dateSubmitted",
      key: "dateSubmitted",
      render: (date) =>
        date ? <Tag color="purple">{moment(date).format("DD MMM YYYY, h:mm A")}</Tag> : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() =>
              notify("info", `Viewing details for ${record.guest_name}`)
            }
          >
            View
          </Button>
          <Button
            icon={<FlagOutlined />}
            size="small"
            danger
            onClick={() =>
              notify("warning", `Flagged response from ${record.guest_name}`)
            }
          >
            Flag
          </Button>
        </Space>
      ),
    },
  ];

  const getDynamicHeight = () => {
    return window.innerHeight - 150;
  };

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <Spin spinning={loading}>
          {/* Header */}
{/* Header */}
{/* Header */}
<div className="page-header page-top-icons d-flex justify-content-between align-items-center flex-wrap mb-3">
  {/* Title + Survey Select (left side) */}
  <div className="d-flex align-items-center gap-3">
    <h3 className="page-title mb-0">{initialSurveyTitle || "Responses"}</h3>
    <div style={{ minWidth: "220px" }}>
      <SurveySelect
        value={filters.surveyId}
        onChange={(option) => {
          const surveyId = option?.value || null;
          setFilters((prev) => ({ ...prev, surveyId }));
        }}
        notify={notify}
      />
    </div>
  </div>

  {/* Right side controls */}
  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
    {/* Date Range (compact) */}
    <DatePicker
      initialSettings={{
        singleDatePicker: false,
        showDropdowns: true,
        startDate: filters.dateRange.startDate.toDate(),
        endDate: filters.dateRange.endDate.toDate(),
        ranges: {
          "Today": [moment().toDate(), moment().toDate()],
          "This Week": [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
          "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
          "Last 7 Days": [moment().subtract(7, "days").toDate(), moment().toDate()],
          "Last 30 Days": [moment().subtract(30, "days").toDate(), moment().toDate()],
        },
        locale: { format: "DD MMM", applyLabel: "Select", cancelLabel: "Cancel" },
      }}
      onCallback={(start, end) =>
        setFilters({
          ...filters,
          dateRange: {
            startDate: moment(start).startOf("day"),
            endDate: moment(end).endOf("day"),
          },
        })
      }
    >
      <div className="input-group" style={{ maxWidth: "160px" }}>
        <input
          type="text"
          className="form-control"
          value={`${filters.dateRange.startDate.format("DD MMM")} - ${filters.dateRange.endDate.format("DD MMM")}`}
          style={{
            height: "36px",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
            padding: "0 6px",
            borderRight: "none",
            backgroundColor: "#fff",
          }}
          readOnly
        />
        <span className="input-group-text" style={{ height: "36px", backgroundColor: "#fff", borderLeft: "none", cursor: "pointer" }}>
          <i className="fa fa-chevron-down" style={{ fontSize: "12px", color: "#495057" }} />
        </span>
      </div>
    </DatePicker>

    {/* Search */}
    <div style={{ minWidth: "200px" }}>
      <Search
        placeholder="Search"
        allowClear
        size="middle"
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
      />
    </div>

    {/* Action Buttons */}
    <div className="d-flex align-items-center gap-2">
      <Button className="btn btn-link" onClick={() => setFilterModalVisible(true)}>
        <i className="las la-filter" />
      </Button>
      <Button className="btn btn-link" ref={maximizeBtnRef}>
        <i className="las la-expand-arrows-alt" />
      </Button>
      <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
        <Button className="custom-btn-drop d-inline-flex align-items-center">
          <i className="la la-download custom-icon me-2" /> Download <DownOutlined className="ms-2" />
        </Button>
      </Dropdown>
      <Button onClick={() => window.history.back()} className="btn add-btn custom-btn ms-2">
        <i className="la la-arrow-left custom-icon" /> Back
      </Button>
    </div>
  </div>
</div>



          <div
            className="table-responsive"
            style={{
              minHeight: "60vh",
              height: "calc(100vh - 150px)",
              overflow: "auto",
            }}
          >
            {filteredData.length === 0 ? (
              <div
                className="no-data-container text-center mt-4"
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>No Data Available</h3>
                <p>No responses found for the selected filters.</p>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                className="table-striped custom-table"
                scroll={{ x: 1200, y: getDynamicHeight() }}
              />
            )}
          </div>

          <Row gutter={16} style={{ marginTop: "20px" }}>
            <Col span={24}>
              <Card title="Response Summary" style={{ borderRadius: "12px", background: "#fafafa" }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <p><strong>Total Responses:</strong> {summaryStats.totalResponses}</p>
                  </Col>
                  <Col span={8}>
                    <p><strong>Average Rating:</strong> {summaryStats.averageRating} ⭐</p>
                  </Col>
                  <Col span={8}>
                    <p><strong>Average NPS:</strong> {summaryStats.averageNPS}/10</p>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <PrintReportPreview
            visible={previewVisible}
            onClose={() => setPreviewVisible(false)}
            reportTitle="Responses"
            propertyName="Your Property Name"
            data={exportData}
            headers={headers}
            footerData={totalsForPageFooter}
            totalColumns={[]}
            onDownloadPDF={handleExportPDF}
            apiEndpoint="responses/generate-pdf"
            apiUrl={apiUrl}
          />
        </Spin>
      </div>
    </div>
  );
};

export default ResponseList;